import { useAppStoreWithOut } from '@/store/modules/app';
import { useLocaleStoreWithOut } from '@/store/modules/locale';
import { useUserStoreWithOut } from '@/store/modules/user';

type AuthStatus = 'initializing' | 'anonymous' | 'authenticated' | 'expired';
type HostSnapshot = {
  authStatus: AuthStatus;
  authEpoch: number;
  userId?: string;
  tenantId?: string;
  permissions?: readonly string[];
  locale: string;
  theme: 'light' | 'dark';
};

type HostCapabilitiesV1 = {
  protocolVersion: 1;
  getSnapshot(): Readonly<HostSnapshot>;
  subscribe(listener: (snapshot: Readonly<HostSnapshot>) => void): () => void;
  getToken(authEpoch: number): string | undefined;
  navigate(path: string): void;
  requestLogin(): void;
};

type SessionState = {
  authStatus: AuthStatus;
  userId?: string;
  tenantId?: string;
  token?: string;
};

const asId = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isSafeInteger(value)) return String(value);
  return undefined;
};

const sameSnapshot = (left: Readonly<HostSnapshot> | undefined, right: Readonly<HostSnapshot>) =>
  left?.authStatus === right.authStatus &&
  left.authEpoch === right.authEpoch &&
  left.userId === right.userId &&
  left.tenantId === right.tenantId &&
  left.locale === right.locale &&
  left.theme === right.theme;

let authEpoch = 0;
let previousSession: SessionState | undefined;
let lastPublished: Readonly<HostSnapshot> | undefined;
let stopStoreSubscriptions: (() => void) | undefined;
const listeners = new Set<(snapshot: Readonly<HostSnapshot>) => void>();

function readSnapshot(): Readonly<HostSnapshot> {
  const userStore = useUserStoreWithOut();
  const appStore = useAppStoreWithOut();
  const localeStore = useLocaleStoreWithOut();
  const token = typeof userStore.getToken === 'string' ? userStore.getToken : undefined;
  const userInfo = userStore.getUserInfo || {};
  const userId = asId(userInfo.userId ?? userInfo.id);
  const tenantId = asId(userInfo.tenantId);
  const authStatus: AuthStatus = userStore.getSessionTimeout
    ? 'expired'
    : !token
    ? 'anonymous'
    : !userId || !tenantId
    ? 'initializing'
    : 'authenticated';

  if (
    !previousSession ||
    previousSession.authStatus !== authStatus ||
    previousSession.userId !== userId ||
    previousSession.tenantId !== tenantId ||
    previousSession.token !== token
  ) {
    authEpoch += 1;
    previousSession = { authStatus, userId, tenantId, token };
  }

  return Object.freeze({
    authStatus,
    authEpoch,
    ...(authStatus === 'authenticated' ? { userId, tenantId } : {}),
    locale:
      typeof localeStore.getLocale === 'string' && localeStore.getLocale
        ? localeStore.getLocale
        : 'zh-CN',
    theme: appStore.getDarkMode === 'dark' ? 'dark' : 'light',
  });
}

function publishSnapshot() {
  const snapshot = readSnapshot();
  if (sameSnapshot(lastPublished, snapshot)) return;
  lastPublished = snapshot;
  listeners.forEach((listener) => listener(snapshot));
}

function startStoreSubscriptions() {
  if (stopStoreSubscriptions) return;
  const userStore = useUserStoreWithOut();
  const appStore = useAppStoreWithOut();
  const localeStore = useLocaleStoreWithOut();
  const options = { detached: true, flush: 'sync' as const };
  const stops = [
    userStore.$subscribe(publishSnapshot, options),
    appStore.$subscribe(publishSnapshot, options),
    localeStore.$subscribe(publishSnapshot, options),
  ];
  stopStoreSubscriptions = () => stops.forEach((stop) => stop());
}

function navigate(path: string) {
  if (!path.startsWith('/') || path.startsWith('//')) return;
  void import('@/router').then(({ router }) => router.push(path)).catch(() => undefined);
}

export const jycBusinessReactHost: HostCapabilitiesV1 = {
  protocolVersion: 1,
  getSnapshot: readSnapshot,
  subscribe(listener) {
    listeners.add(listener);
    startStoreSubscriptions();
    return () => {
      listeners.delete(listener);
      if (!listeners.size) {
        stopStoreSubscriptions?.();
        stopStoreSubscriptions = undefined;
      }
    };
  },
  getToken(epoch) {
    const snapshot = readSnapshot();
    if (snapshot.authStatus !== 'authenticated' || snapshot.authEpoch !== epoch) return undefined;
    const token = useUserStoreWithOut().getToken;
    return typeof token === 'string' && token.trim() ? token : undefined;
  },
  navigate,
  requestLogin() {
    navigate('/login');
  },
};
